<script setup lang="ts">
import { computed, capitalize } from "vue";
import { useRoute } from "vue-router";
import { isNavbarItemActive, type NavbarItem } from "@/components/shared/navbar/useNavbarItems.ts";

const props = defineProps<{
  items: NavbarItem[];
  compacted: boolean;
}>();

const route = useRoute();

const visibleItems = computed(() => props.items.filter((item) => !item.hidden));

const isActive = (item: NavbarItem) => {
  if (item.url === "/") {
    return (
      route.path === "/" || route.path === "/dashboard" || route.path.startsWith("/dashboard/")
    );
  }

  return isNavbarItemActive(item, route.path, route.query.returnTo as string | undefined);
};
</script>

<template>
  <v-list
    class="nav-items-list tw:flex tw:items-stretch tw:ml-6! tw:h-full tw:py-0! tw:z-layer-21"
    :class="{ 'nav-items-list--compact': compacted }"
  >
    <v-list-item
      v-for="item in visibleItems"
      :key="item.url"
      :active="isActive(item)"
      :data-tour="item.url === '/organization' ? 'tour-practices-nav-item' : undefined"
      :disabled="item.disabled"
      :ripple="false"
      :to="item.to ?? item.url"
      class="nav-item tw:relative tw:h-full tw:min-h-0! tw:overflow-visible!"
      :class="{ 'nav-item--active': isActive(item) }"
    >
      <v-list-item-title class="nav-item__label">
        <span class="nav-item__inner tw:inline-flex tw:items-center">
          <span>{{ capitalize(item.title) }}</span>
          <v-badge
            v-if="item.badgeNumber && item.badgeNumber > 0"
            inline
            :color="item.badgeColor ?? 'secondary'"
            :content="item.badgeNumber"
            max="99"
            class="badge"
          />
        </span>
      </v-list-item-title>

      <span class="nav-item__indicator" />
    </v-list-item>
  </v-list>
</template>

<style scoped lang="scss">
.nav-items-list {
  overflow: visible !important;
  background: transparent !important;

  :deep(.v-list-item .v-list-item__overlay) {
    display: none;
  }

  :deep(.v-list-item__content) {
    height: 100%;
    overflow: visible;
  }
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 2px;
  padding: 0 !important;
  border-radius: 0;
  color: rgba(var(--v-theme-on-surface), 0.65);
  transition: color 0.16s ease;

  &:hover {
    color: rgb(var(--v-theme-on-surface));

    .nav-item__inner {
      background-color: rgba(var(--v-theme-on-surface), 0.055);
    }
  }

  &.nav-item--active {
    color: rgb(var(--v-theme-on-surface));

    .nav-item__label {
      font-weight: 700;
    }

    .nav-item__inner {
      background-color: rgba(var(--v-theme-secondary), 0.09);
    }

    .nav-item__indicator {
      background: rgb(var(--v-theme-secondary));
    }
  }
}

.nav-item__label {
  display: flex;
  align-items: center;
  height: 100%;
  overflow: visible;
  font-size: 13px !important;
  font-weight: 600;
  letter-spacing: -0.006em;
  line-height: 1;
}

.nav-item__inner {
  height: 34px;
  padding: 0 13px;
  border-radius: 8px;
  white-space: nowrap;
  transition: background-color 0.16s ease;
}

.nav-items-list--compact .nav-item__inner {
  height: 30px;
}

.nav-item__indicator {
  position: absolute;
  right: 13px;
  bottom: 0;
  left: 13px;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: transparent;
  transition: background-color 0.16s ease;
  pointer-events: none;
}

.badge {
  margin-inline-start: 7px;

  :deep(.v-badge__wrapper) {
    margin-inline-end: 0;
  }

  :deep(.v-badge__badge) {
    min-width: 18px;
    height: 17px;
    padding: 0 5px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 700;
    line-height: 17px;
  }
}
</style>
